import { useState, useEffect } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import type { MailAccountGroupItem } from '@/types/api';

const PRESET_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
];

interface Props {
  open: boolean;
  group?: MailAccountGroupItem | null;
  onClose: () => void;
  onSave: (data: { name: string; color: string }) => Promise<void>;
  loading?: boolean;
}

export function GroupEditorModal({ open, group, onClose, onSave, loading }: Props) {
  const [name, setName] = useState('');
  const [color, setColor] = useState(PRESET_COLORS[0]!);

  useEffect(() => {
    if (open) {
      setName(group?.name ?? '');
      setColor(group?.color ?? PRESET_COLORS[0]!);
    }
  }, [open, group]);

  async function handleSave() {
    if (!name.trim()) return;
    await onSave({ name: name.trim(), color });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={group ? 'Modifier le groupe' : 'Nouveau groupe'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSave} loading={loading} disabled={!name.trim()}>
            {group ? 'Enregistrer' : 'Créer'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <Label>Nom</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="FR Prospects"
            autoFocus
          />
        </div>
        <div>
          <Label>Couleur</Label>
          <div className="flex items-center gap-2 mt-1">
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
                style={{
                  backgroundColor: c,
                  borderColor: color === c ? 'rgba(0,0,0,0.4)' : 'transparent',
                }}
                onClick={() => setColor(c)}
              />
            ))}
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-6 h-6 rounded cursor-pointer border border-gray-200"
              title="Couleur personnalisée"
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
}
